import { useState, useMemo } from 'react'
import { Container, Row, Col } from 'react-bootstrap'
import AnalyticsDateFilter from './AnalyticsDateFilter'
import SummaryCards from './SummaryCards'
import TimeSeriesChart from './TimeSeriesChart'
import BreakdownPieChart from './BreakdownPieChart'
import TopItemsBarChart from './TopItemsBarChart'

const computeDates = (months) => {
  const end = new Date()
  const start = new Date()
  start.setMonth(start.getMonth() - months)
  return {
    start_date: start.toISOString().split('T')[0],
    end_date: end.toISOString().split('T')[0]
  }
}

const AnalyticsDashboard = () => {
  const [months, setMonths] = useState(1)
  const [grouping, setGrouping] = useState('day')
  const [municipalityIds, setMunicipalityIds] = useState([])

  const params = useMemo(() => {
    const dates = computeDates(months)
    return { ...dates, grouping, municipality_ids: municipalityIds }
  }, [months, grouping, municipalityIds])

  return (
    <Container>
      <h1 className="mb-4">Usage</h1>

      <AnalyticsDateFilter
        months={months}
        grouping={grouping}
        municipalityIds={municipalityIds}
        onMonthsChange={setMonths}
        onGroupingChange={setGrouping}
        onMunicipalityChange={setMunicipalityIds}
      />

      <SummaryCards params={params} />

      <Row className="mb-4">
        <Col lg={6} className="mb-4">
          <TimeSeriesChart
            title="Visits Over Time"
            subtitle="Total user sessions initiated across all municipalities"
            endpoint="visits_over_time"
            params={params}
            yAxisTitle="Visits"
            color="#1E2E48"
          />
        </Col>
        <Col lg={6} className="mb-4">
          <TimeSeriesChart
            title="Unique Visitors Over Time"
            subtitle="Distinct users who logged in during each period"
            endpoint="unique_visitors_over_time"
            params={params}
            yAxisTitle="Unique Visitors"
            color="#27AB83"
          />
        </Col>
      </Row>

      <Row className="mb-4">
        <Col lg={6} className="mb-4">
          <TimeSeriesChart
            title="Events Over Time"
            subtitle="User interactions such as clicks, exports, and filter changes"
            endpoint="events_over_time"
            params={params}
            yAxisTitle="Events"
            color="#E9C46A"
          />
        </Col>
        <Col lg={6} className="mb-4">
          <TimeSeriesChart
            title="Page Views Over Time"
            subtitle="Total pages navigated to across the application"
            endpoint="page_views_over_time"
            params={params}
            yAxisTitle="Page Views"
            color="#38BEC9"
          />
        </Col>
      </Row>

      <Row className="mb-4">
        <Col lg={6} className="mb-4">
          <TimeSeriesChart
            title="Average Session Duration"
            subtitle="Average active time per session, measured from activity heartbeats"
            endpoint="session_duration_over_time"
            params={params}
            yAxisTitle="Seconds"
            color="#9F7AEA"
          />
        </Col>
      </Row>

      <Row className="mb-4">
        <Col lg={6} className="mb-4">
          <TopItemsBarChart
            title="Top Pages"
            subtitle="Most frequently visited pages ranked by total views"
            endpoint="top_pages"
            params={params}
            categoryKey="path"
            color="#E9C46A"
          />
        </Col>
        <Col lg={6} className="mb-4">
          <TopItemsBarChart
            title="Top Events"
            subtitle="Most common user actions ranked by frequency"
            endpoint="top_events"
            params={params}
            categoryKey="name"
            color="#38BEC9"
          />
        </Col>
      </Row>

      <Row className="mb-4">
        <Col lg={4} className="mb-4">
          <BreakdownPieChart
            title="Browser Distribution"
            subtitle="Share of visits by web browser"
            endpoint="browser_breakdown"
            params={params}
          />
        </Col>
        <Col lg={4} className="mb-4">
          <BreakdownPieChart
            title="Operating System Distribution"
            subtitle="Share of visits by operating system"
            endpoint="os_breakdown"
            params={params}
          />
        </Col>
        <Col lg={4} className="mb-4">
          <BreakdownPieChart
            title="Device Type Distribution"
            subtitle="Share of visits by device type"
            endpoint="device_type_breakdown"
            params={params}
          />
        </Col>
      </Row>
    </Container>
  )
}

export default AnalyticsDashboard
